import { PLAYER_LAST_SEEN_INTERVAL_SECONDS } from '@dayon/contracts';
import { Button, DataTable, EmptyState, Icon, Panel, Tag, type Column } from '@dayon/design-system';
import { codeAsWords } from '../../../lib/audit-words.ts';

/**
 * WHERE THIS ACCOUNT IS SIGNED IN, AND THE THREE THINGS AN OPERATOR CAN DO.
 *
 * The four routes behind this tab were built, audited, reason-coded and
 * documented before any of it — and nothing called them. The module's own
 * header describes the call they exist to answer: a player telephones to say
 * somebody else is in their account. Until this tab, the console could suspend
 * the account and nothing more, which bars the OWNER while the attacker's live
 * session keeps working to its own expiry.
 *
 * ── WHY THE SESSION LIST IS WORTH A TABLE ────────────────────────────────
 *
 * It is the evidence the decision is made on. An operator looking at "signed in
 * from two places, one of them an hour ago on a browser this account has never
 * used" is making a different call from one looking at a count. The IP and the
 * user agent are shown raw, because a parsed summary is a guess and this is the
 * one screen where somebody will read the string itself.
 *
 * ── AND WHY IT SAYS WHEN IT IS NOT THE WHOLE LIST ────────────────────────
 *
 * The endpoint caps at `historyLimit` rows and reports whether it hit the cap.
 * "These are all the sessions" is a claim an operator acts on during an
 * investigation, so a truncated list has to say so rather than look complete.
 */

export interface SessionRow {
  id: string;
  live: boolean;
  startedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  userAgent: string | null;
  ip: string | null;
  /** When the session was last used, to the nearest few minutes; null if never since sign-in. */
  lastSeenAt: string | null;
  lastIp: string | null;
  /** The retention period has passed and the addresses were blanked on purpose. */
  redacted: boolean;
}

export interface SecurityView {
  player: {
    hasCredential: boolean;
    credentialStatus: string | null;
    /** The account's own standing — SUSPENDED refuses a sign-in whatever the password. */
    status: string;
    emailVerified: boolean;
    passwordUpdatedAt: string | null;
    /**
     * When an operator's forced reset took the password away, while no password has been set
     * since; null when the password on the account is one its player set. The server derives it
     * (player-admin/routes.ts), because `passwordUpdatedAt` moves on a forced reset too.
     */
    passwordTakenAt: string | null;
    brandName: string;
    /**
     * False while the account's brand is off the floor. The sign-in route finds the brand from the
     * site's host, and only a brand on the floor answers there, so every sign-in is refused before
     * the password is read. A live session goes on answering: the session guard does not read it.
     */
    brandActive: boolean;
  };
  sessions: SessionRow[];
  liveCount: number;
  historyLimit: number;
  truncated: boolean;
}

/**
 * Operator words for what ended a session — one for every reason the API writes (held to them by
 * session-end-words.test.tsx). Never the stored constant: `endedByWords` says one this map has not
 * caught up with in words.
 */
export const SESSION_ENDED_BY: Readonly<Record<string, string>> = {
  SIGNED_OUT: 'The player signed out',
  /* One session at a time (PAGCOR item 14): signing in ends every other. */
  SUPERSEDED_BY_SIGN_IN: 'A newer sign-in ended it',
  PLAYER_SIGNED_OUT_ALL: 'The player ended every session',
  PASSWORD_RESET: 'A password reset ended it',
  PLAYER_CHANGED_PASSWORD: 'The player changed the password',
  ADMIN_SIGNED_OUT: 'An administrator ended it',
  FORCED_PASSWORD_RESET: 'A forced password reset ended it',
  PLAYER_SUSPENDED: 'Suspending the account ended it',
};

const endedByWords = (reason: string): string => SESSION_ENDED_BY[reason] ?? codeAsWords(reason);

/**
 * WHETHER THE PLAYER CAN SIGN IN, as the server reports it: the first refusal, in the sign-in route's
 * own order. A brand off the floor is refused before anything else is read — the route finds no
 * brand for the site; then a suspended account (or a credential that is not active) is refused
 * whatever the password; and otherwise a password a forced reset took away refuses every sign-in
 * until a new one is set. Only when none of them stands does the password let the player in.
 *
 * Only `false` is read as off the floor, so an answer without the field claims nothing about it.
 */
export type SignInStanding = 'BRAND_OFF' | 'SUSPENDED' | 'PASSWORD_TAKEN' | 'PASSWORD_WORKS';

/** Refused at sign-in whatever the password — and, unlike the brand, at a live session's next request. */
const accountSuspended = (player: SecurityView['player']): boolean =>
  player.status === 'SUSPENDED' || player.credentialStatus !== 'ACTIVE';

export const signInStanding = (player: SecurityView['player']): SignInStanding =>
  player.brandActive === false
    ? 'BRAND_OFF'
    : accountSuspended(player)
      ? 'SUSPENDED'
      : player.passwordTakenAt !== null
        ? 'PASSWORD_TAKEN'
        : 'PASSWORD_WORKS';

export function SecurityTab({
  view,
  failed,
  canRestrict,
  canResend,
  onSignOut,
  onForceReset,
  onResendVerification,
  formatMoment,
}: {
  view: SecurityView | null;
  failed: boolean;
  canRestrict: boolean;
  canResend: boolean;
  onSignOut: () => void;
  onForceReset: () => void;
  onResendVerification: () => void;
  /** Manila time, as the Logs tab and the exported log read it. */
  formatMoment: (iso: string) => string;
}) {
  const columns: Column<SessionRow>[] = [
    {
      key: 'state',
      header: 'State',
      render: (row) =>
        row.live ? (
          <Tag tone="accent">Live</Tag>
        ) : (
          <Tag tone="quiet">{row.revokedAt ? 'Ended' : 'Expired'}</Tag>
        ),
    },
    { key: 'startedAt', header: 'Signed in', render: (row) => formatMoment(row.startedAt) },
    {
      key: 'ip',
      header: 'From',
      /* Raw. A geo-lookup would be a guess presented as a fact, and there is no
         geolocation in this platform to make it with. */
      render: (row) =>
        row.redacted ? (
          <span className="text-subtle">Removed after the retention period</span>
        ) : (
          (row.ip ?? <span className="text-subtle">not recorded</span>)
        ),
    },
    {
      key: 'lastSeenAt',
      header: 'Last active',
      render: (row) =>
        row.lastSeenAt === null ? (
          '—'
        ) : (
          <>
            {formatMoment(row.lastSeenAt)}
            {row.lastIp !== null && row.lastIp !== row.ip && (
              <div className="cell-sub">from {row.lastIp}</div>
            )}
          </>
        ),
    },
    {
      key: 'userAgent',
      header: 'Device',
      render: (row) =>
        row.userAgent === null ? (
          <span className="text-subtle">not recorded</span>
        ) : (
          <span className="cell-sub" title={row.userAgent}>
            {row.userAgent}
          </span>
        ),
    },
    {
      key: 'ended',
      header: 'Ended',
      render: (row) =>
        row.live ? (
          <span className="text-subtle">still open until {formatMoment(row.expiresAt)}</span>
        ) : row.revokedAt ? (
          <>
            {formatMoment(row.revokedAt)}
            {row.revokedReason !== null && (
              <div className="cell-sub">{endedByWords(row.revokedReason)}</div>
            )}
          </>
        ) : (
          formatMoment(row.expiresAt)
        ),
    },
  ];

  if (failed) {
    return (
      <Panel title="Sign-in and sessions">
        <EmptyState icon="warning-circle" title="Could not read this account's sessions">
          The platform did not answer. Nothing is shown rather than a list that might be out of
          date — during an investigation a stale session list is worse than none.
        </EmptyState>
      </Panel>
    );
  }

  if (view === null) {
    return (
      <Panel title="Sign-in and sessions">
        <div className="skeleton" style={{ height: '180px' }}>
          &nbsp;
        </div>
      </Panel>
    );
  }

  const { player, sessions, liveCount, historyLimit, truncated } = view;
  const standing = signInStanding(player);
  const suspended = accountSuspended(player);
  const takenAt = player.passwordTakenAt === null ? null : formatMoment(player.passwordTakenAt);

  return (
    <Panel
      title="Sign-in and sessions"
      actions={
        <div className="row-actions">
          <Button
            small
            disabled={!canRestrict || !player.hasCredential}
            onClick={onSignOut}
            aria-label={
              !canRestrict
                ? 'End every session — your role cannot do this'
                : !player.hasCredential
                  ? 'End every session — this account has never been signed into'
                  : undefined
            }
          >
            <Icon name="sign-out" /> End every session
          </Button>
          <Button
            small
            disabled={!canRestrict || !player.hasCredential}
            onClick={onForceReset}
            aria-label={
              !canRestrict
                ? 'Reset the password — your role cannot do this'
                : !player.hasCredential
                  ? 'Reset the password — this account has no password to reset'
                  : undefined
            }
          >
            <Icon name="key" /> Reset the password
          </Button>
          <Button
            small
            disabled={!canResend || player.emailVerified}
            onClick={onResendVerification}
            aria-label={
              !canResend
                ? 'Send a confirmation link — your role cannot do this'
                : player.emailVerified
                  ? 'Send a confirmation link — this address is already confirmed'
                  : undefined
            }
          >
            <Icon name="envelope" /> Send a confirmation link
          </Button>
        </div>
      }
    >
      {/*
        NEVER SIGNED IN is a different fact from NO LIVE SESSIONS, and the
        difference matters here more than anywhere: most of this platform's
        accounts were opened on a player's behalf and have no credential row at
        all. Without this branch every one of them renders as a player who has
        just been signed out of everything.
      */}
      {!player.hasCredential ? (
        <EmptyState icon="user-circle-dashed" title="This account has never been signed into">
          There is no password on it and no session has ever been opened. It was created on the
          player&apos;s behalf, and they become able to sign in the first time they set a
          password. Until then there is nothing here to end and nothing to reset.
        </EmptyState>
      ) : (
        <>
          <div className="stack">
            {/*
              ONE SENTENCE PER STATE THE SERVER REPORTS. "The password still works" used to be
              printed whenever no session was live, with no other check — and straight after a
              forced reset it sat beside the dialog saying the password no longer works. A brand
              off the floor refuses every sign-in to its site before the password is read; a
              suspended account is refused at sign-in whatever the password; and a password a
              forced reset took away refuses everybody until the player sets a new one from a
              reset link. Only when none of them stands does the password still let them in, and
              the brand's sentence names whichever of the others stand as well.

              A LIVE SESSION is the session guard's question, not the sign-in route's: it refuses
              a suspended account at the next request and does not read the brand at all.
            */}
            <div className={liveCount > 0 ? 'notice' : 'notice notice-quiet'}>
              <Icon name={liveCount > 0 ? 'device-mobile' : 'info'} />
              <div>
                {liveCount > 0 && suspended ? (
                  <p>
                    <strong>
                      {liveCount === 1
                        ? 'One session is still open'
                        : `${liveCount} sessions are still open`}
                    </strong>
                    , but the account is suspended, so the platform refuses{' '}
                    {liveCount === 1 ? 'it' : 'each of them'} at its next request.
                  </p>
                ) : liveCount > 0 && standing === 'BRAND_OFF' ? (
                  <p>
                    <strong>
                      {liveCount === 1 ? 'One session is live' : `${liveCount} sessions are live`}
                    </strong>{' '}
                    — this account is signed in right now, but {player.brandName} is off the floor:
                    once {liveCount === 1 ? 'it ends' : 'they end'}, the player cannot sign back in
                    while the brand stays off.
                  </p>
                ) : liveCount > 0 ? (
                  <p>
                    <strong>
                      {liveCount === 1 ? 'One session is live' : `${liveCount} sessions are live`}
                    </strong>{' '}
                    — this account is signed in and usable right now.
                  </p>
                ) : standing === 'BRAND_OFF' ? (
                  <p>
                    No session is live, and the player cannot sign in: {player.brandName}, the brand
                    this account belongs to, is off the floor, and its site refuses every sign-in
                    while it is.
                    {suspended && ' The account is also suspended.'}
                    {takenAt !== null &&
                      ` A forced reset also took the password away on ${takenAt}, and no new one has been set since.`}
                  </p>
                ) : standing === 'SUSPENDED' ? (
                  <p>
                    No session is live, and the player cannot sign in: the account is suspended.
                    {takenAt !== null &&
                      ` A forced reset also took the password away on ${takenAt}, and no new one has been set since.`}
                  </p>
                ) : standing === 'PASSWORD_TAKEN' ? (
                  <p>
                    No session is live, and nobody can sign in to this account: a forced reset took
                    the password away on {takenAt} and no new one has been set since. The player
                    gets back in by setting a new password from a reset link sent to the address on
                    the account.
                  </p>
                ) : (
                  <p>
                    No session is live. The password still works, so the player can sign in again
                    with it.
                  </p>
                )}
                <p className="text-subtle">
                  {takenAt !== null
                    ? `Password taken away ${takenAt}`
                    : `Password last set ${
                        player.passwordUpdatedAt === null
                          ? 'never'
                          : formatMoment(player.passwordUpdatedAt)
                      }`}
                  . Email address{' '}
                  {player.emailVerified ? 'confirmed' : 'not confirmed — they cannot be emailed a '}
                  {player.emailVerified ? '' : 'reset link they can trust until it is'}.
                </p>
              </div>
            </div>

            {truncated && (
              <div className="notice notice-warn">
                <Icon name="warning" />
                <div>
                  Only the {historyLimit} most recent sessions are shown, and there are more. This
                  is not the whole history — do not read it as one.
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 'var(--space-3)' }}>
            <DataTable
              columns={columns}
              rows={sessions}
              rowKey={(row) => row.id}
              caption="Every recorded sign-in for this account, newest first"
              empty="No session has ever been opened on this account."
            />
            <p className="text-subtle">
              Last active is updated at most once every {PLAYER_LAST_SEEN_INTERVAL_SECONDS / 60}{' '}
              minutes while a session is used. A dash means it has not been used since it signed
              in, or it signed in before this was recorded. Times are Manila time.
            </p>
          </div>
        </>
      )}
    </Panel>
  );
}
